import React from 'react';
import { motion, Variants } from 'framer-motion';
import { useContent } from '../context/ContentContext';

const Hero: React.FC = () => {
  const { content } = useContent();
  const { companyNameCn, companyNameEn, tagline, subTagline, logoUrl, backgroundImage } = content.hero;

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <header id="home" className="relative h-screen min-h-[600px] w-full overflow-hidden bg-slate-950 flex items-center">
      <div className="absolute inset-0 z-0">
        <img 
          src={backgroundImage || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=1920"} 
          alt="Modern Architecture" 
          className="w-full h-full object-cover opacity-30"
          fetchPriority="high" 
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-4xl"
        >
          <motion.div variants={itemVariants} className="mb-12 flex items-center gap-8">
            {logoUrl && (
              <div className="relative">
                {/* 确保没有任何背景色或模糊滤镜，仅保留极轻微的投影增强对比度 */}
                <img 
                  src={logoUrl} 
                  alt="Jianyun Logo" 
                  className="w-28 h-auto md:w-40 relative z-10 drop-shadow-lg transition-transform duration-500 hover:scale-105" 
                />
              </div>
            )}
            <div className="w-px h-24 bg-gradient-to-b from-transparent via-[#D4AF37]/50 to-transparent hidden md:block"></div>
            <div className="hidden md:block">
              <span className="text-[#D4AF37] font-black tracking-[0.5em] uppercase text-[11px] block mb-2 opacity-90">JIANYUN TAX AGENTS</span>
              <span className="text-white/40 font-mono text-xs tracking-widest uppercase">Professional Integrity & Excellence</span>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="w-24 h-1.5 bg-[#D4AF37] mb-10 shadow-[0_0_20px_rgba(212,175,55,0.6)]"></motion.div>
          
          <motion.h1 
            variants={itemVariants} 
            className="text-[5.5vw] sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight leading-[1.2] whitespace-nowrap"
          >
            {companyNameCn}
          </motion.h1>
          
          <motion.h2 
            variants={itemVariants} 
            className="text-[10px] sm:text-sm md:text-xl text-[#D4AF37] font-bold mb-10 tracking-[0.1em] sm:tracking-[0.2em] uppercase opacity-90 max-w-full break-words leading-relaxed"
          >
            {companyNameEn}
          </motion.h2>
          
          <motion.p variants={itemVariants} className="text-gray-200 text-lg md:text-2xl max-w-2xl leading-relaxed mb-12 border-l-4 border-[#D4AF37] pl-8 bg-white/5 backdrop-blur-md py-6 rounded-r-3xl text-justify">
            {tagline} <br/>
            <span className="text-gray-400 text-sm md:text-lg mt-3 block font-normal tracking-wide italic opacity-70">{subTagline}</span>
          </motion.p>
          
          <motion.div variants={itemVariants} className="flex flex-wrap gap-5">
            <a href="#services" className="px-12 py-5 bg-[#D4AF37] text-slate-950 font-black rounded-2xl hover:bg-white transition-all duration-300 shadow-xl transform hover:-translate-y-1">
              核心业务
            </a>
            <a href="#about" className="px-12 py-5 border border-white/20 text-white font-bold rounded-2xl hover:bg-white/10 transition-all duration-300 backdrop-blur-xl">
              了解我们
            </a>
          </motion.div>
        </motion.div>
      </div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce hidden md:block"
      >
         <div className="w-7 h-12 border-2 border-white/20 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#D4AF37] rounded-full shadow-[0_0_10px_#D4AF37]"></div>
         </div>
      </motion.div>
    </header>
  );
};

export default Hero;